import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeSlash, ArrowRight, PaperPlaneTilt, Trash } from "@phosphor-icons/react";
import { money } from "@/lib/money";
import ReviewPanel from "@/components/ReviewPanel";


const Row = ({ label, value, mono = false }) => (
  <div className="py-2 grid grid-cols-3 gap-3 border-b border-slate-100 last:border-b-0">
    <div className="col-span-1 text-xs text-slate-500 uppercase tracking-widest font-semibold">{label}</div>
    <div className={`col-span-2 text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate-300">—</span>}</div>
  </div>
);

const Section = ({ title, children, right }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5 mb-4">
    <div className="flex items-center justify-between mb-3">
      <div className="tinylabel">{title}</div>
      {right}
    </div>
    <div>{children}</div>
  </div>
);

export default function PacketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [p, setP] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/contractor-packets/${id}`).then((r) => setP(r.data));
  useEffect(() => { load(); }, [id]);

  const reveal = async () => {
    try {
      const { data } = await api.get(`/contractor-packets/${id}/reveal`);
      setFull(data);
      setRevealed(true);
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contractor-packets/${id}/resend`);
      toast.success(`Packet resent to ${data.to}.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally { setBusy(false); }
  };

  const convert = async () => {
    if (!window.confirm(`Create a Worker record for ${p.worker_name}?\n\nThey'll appear in the Workers list and can be added to assignments. This does NOT create a project or assignment — you'll set those up when the project is confirmed.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/contractor-packets/${id}/convert-to-worker`);
      toast.success(`Worker created (id ${data.worker_id.slice(0, 8)}…).`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally { setBusy(false); }
  };

  const createAssignment = async () => {
    if (!window.confirm(`Create the assignment for ${p.worker_name} on ${p.project_name}?\n\nUses the pay terms from this packet and the project's bill rate, then sends the assignment notice (email + text with login details).`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/contractor-packets/${id}/create-assignment`);
      const n = data.notice || {};
      toast.success(`Assignment created on ${data.project_name}. ${n.ok ? `Notice sent to ${data.worker_name}${n.login_created ? " (login created)" : ""}.` : `Notice NOT sent: ${n.error || "unknown error"} — use Resend notice on the Assignments tab.`}`, { duration: 9000 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this packet permanently?\n\nAny submitted worker data will be erased. This cannot be undone.")) return;
    try {
      await api.delete(`/contractor-packets/${id}`);
      toast.success("Packet deleted.");
      nav("/packets");
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    }
  };

  if (!p) return <div className="p-8">Loading…</div>;

  // Fields shown depend on revealed state.
  const view = revealed && full ? full : p;
  const tax_id_display = revealed && full?.tax_id ? full.tax_id : (p.tax_id_masked || "—");
  const routing_display = revealed && full?.ach_routing ? full.ach_routing : (p.ach_routing_masked || "—");
  const account_display = revealed && full?.ach_account ? full.ach_account : (p.ach_account_masked || "—");

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      <Link to="/packets" data-testid="back-to-packets" className="text-slate-500 hover:text-slate-900 text-sm flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> All packets
      </Link>

      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="tinylabel">Packet</div>
          <h1 className="text-2xl font-black">{p.company_name}</h1>
          <div className="text-sm text-slate-500 mt-1">Proposed for <b>{p.worker_email}</b></div>
        </div>
        <StatusPill status={p.status} />
      </div>

      <Section title="Assignment terms">
        <Row label="Client" value={p.company_name} />
        <Row label="Project" value={p.project_id
          ? <span data-testid="packet-project-link"><Link to="/projects" className="text-green-700 hover:underline font-semibold">{p.project_name || "Linked project"}</Link>
              <span className={`ml-2 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${p.auto_assign ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                {p.auto_assign ? "auto-assign on" : "auto-assign off"}</span></span>
          : <span className="text-slate-400">Not linked — create the assignment manually once the project exists</span>} />
        <Row label="Location" value={p.location} />
        <Row label="Scope" value={p.description} />
        <Row label="Hourly rate" value={<span className="font-mono">{money(p.pay_rate)} / hr</span>} />
        {p.per_diem_applies && <Row label="Per diem" value={<span className="font-mono">{money(p.per_diem_rate)} / day</span>} />}
        <Row label="Anticipated start" value={p.anticipated_start_date} />
        <Row label="Anticipated duration" value={p.anticipated_duration} />
        <Row label="Client contact" value={`${p.contact_name} · ${p.contact_phone}`} />
        <Row label="Sent by" value={p.sent_by} />
        <Row label="Sent at" value={p.sent_at ? (p.sent_at || "").replace("T", " ").slice(0, 19) + " UTC" : "— not sent yet"} />
        {p.sales_rep_id && <Row label="Submitted by" value={`Sales rep${p.submitted_by ? ` (${p.submitted_by})` : ""}`} />}
      </Section>

      {isAdmin && ["pending_review", "changes_requested"].includes(p.status) && (
        <ReviewPanel kind="packet" doc={p} repName={p.submitted_by} onDone={load} />
      )}
      {!isAdmin && ["pending_review", "changes_requested"].includes(p.status) && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4 text-sm text-amber-900" data-testid="review-readonly-note">
          Submitted by a sales rep — waiting for admin approval before it goes to the worker.
        </div>
      )}

      {p.status === "sent" && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4 text-sm text-amber-900">
          <b>Waiting on the worker.</b> They received an email at <b>{p.worker_email}</b> with a link to review and complete the packet.
          {isAdmin && (
            <button onClick={resend} disabled={busy} data-testid="resend-packet-btn"
              className="ml-3 inline-flex items-center gap-1 text-amber-900 underline font-semibold disabled:opacity-60">
              <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Sending…" : "Resend email"}
            </button>
          )}
        </div>
      )}

      {p.status === "completed" || p.status === "converted" ? (
        <>
          <Section title="Legal acceptance" right={
            <div className="text-xs text-emerald-700 font-semibold">✓ Accepted {(p.accepted_at || "").slice(0, 10)}</div>
          }>
            <Row label="Signed by (typed)" value={p.signature_name} />
            <Row label="Signature IP" value={p.signature_ip} mono />
            <Row label="Signed at" value={(p.signature_at || "").replace("T", " ").slice(0, 19) + " UTC"} />
          </Section>

          <Section title="Personal information">
            <Row label="Full name" value={p.worker_name} />
            <Row label="Address" value={p.worker_address} />
            <Row label="Phone" value={p.worker_phone} mono />
            <Row label="Email" value={p.worker_email_confirmed} />
            <Row label="Emergency contact" value={p.emergency_contact_name} />
            <Row label="Emergency phone" value={p.emergency_contact_phone} mono />
          </Section>

          <Section title="Tax &amp; banking (sensitive)" right={
            isAdmin && (
              revealed
                ? <button onClick={() => { setRevealed(false); setFull(null); }} data-testid="hide-sensitive-btn"
                          className="text-slate-600 hover:text-slate-900 text-xs font-semibold flex items-center gap-1">
                    <EyeSlash size={14} /> Hide values
                  </button>
                : <button onClick={reveal} data-testid="reveal-sensitive-btn"
                          className="text-green-700 hover:text-green-900 text-xs font-semibold flex items-center gap-1">
                    <Eye size={14} /> Reveal (audit-logged)
                  </button>
            )
          }>
            <Row label={p.tax_type === "ein" ? "EIN" : "SSN"} value={tax_id_display} mono />
            <Row label="Bank" value={p.bank_name} />
            <Row label="Account type" value={p.account_type} />
            <Row label="Routing number" value={routing_display} mono />
            <Row label="Account number" value={account_display} mono />
          </Section>

          <Section title="W-9">
            <Row label="Legal name" value={p.w9_legal_name} />
            <Row label="Business name" value={p.w9_business_name} />
            <Row label="Tax classification" value={p.w9_tax_classification} />
            <Row label="Address" value={p.w9_address} />
          </Section>

          {isAdmin && p.status === "completed" && (
            <div className="bg-emerald-50 border-2 border-emerald-600 rounded-md p-5 mb-4">
              <div className="font-black text-emerald-900">Ready to create the Worker record?</div>
              <p className="text-sm text-emerald-900 mt-1">This adds them to your <b>Workers</b> list with their banking info encrypted and status set to Active. They can then be assigned to any project you set up later.</p>
              <button onClick={convert} disabled={busy} data-testid="convert-packet-btn"
                className="mt-3 h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
                <ArrowRight size={16} weight="bold" /> {busy ? "Creating…" : "Create Worker record"}
              </button>
            </div>
          )}
          {p.status === "converted" && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4 text-sm text-slate-700">
              ✓ Converted to Worker <span className="font-mono">{p.worker_id_created?.slice(0, 8)}…</span> on {(p.converted_at || "").slice(0, 10)}. See them in the <Link className="text-green-700 hover:underline" to="/workers">Workers</Link> list.
              {p.auto_assign_note && <span className="block text-xs text-slate-500 mt-0.5">Note: {p.auto_assign_note}.</span>}
            </div>
          )}

          {p.assignment_id_created && (
            <div className="bg-emerald-50 border border-emerald-300 rounded-md p-4 mb-4 text-sm text-emerald-900" data-testid="packet-assignment-created">
              <div className="font-bold">✓ Assignment created {p.auto_assign_status === "created" && (p.auto_assign_at || "") ? `on ${p.auto_assign_at.slice(0, 10)}` : ""} — {p.project_name}</div>
              <div className="text-xs mt-0.5">
                {p.assignment_notice?.ok
                  ? <>Assignment notice sent (email{p.assignment_notice.sms_sent ? " + text" : ""}{p.assignment_notice.login_created ? ", login created" : ", existing login"}).</>
                  : <span className="text-amber-800">Assignment notice NOT sent{p.assignment_notice?.error ? `: ${p.assignment_notice.error}` : ""} — use <b>Resend notice</b> on the Assignments tab.</span>}
                {" "}<Link to="/assignments" className="underline font-semibold">Open assignments →</Link>
              </div>
            </div>
          )}
          {isAdmin && p.project_id && !p.assignment_id_created && (
            <div className={`border-2 rounded-md p-5 mb-4 ${p.auto_assign_status === "held" ? "bg-amber-50 border-amber-500" : "bg-emerald-50 border-emerald-600"}`} data-testid="packet-create-assignment-box">
              {p.auto_assign_status === "held" ? (
                <>
                  <div className="font-black text-amber-900">Assignment not created automatically</div>
                  <p className="text-sm text-amber-900 mt-1"><b>Reason:</b> {p.auto_assign_error}</p>
                  <p className="text-xs text-amber-800 mt-1">Fix the cause (e.g. reactivate the project, end the duplicate assignment) and try again.</p>
                </>
              ) : (
                <>
                  <div className="font-black text-emerald-900">Create the assignment on {p.project_name}?</div>
                  <p className="text-sm text-emerald-900 mt-1">Uses this packet's pay terms and the project's bill rate{p.worker_id || p.worker_id_created ? "" : ", and creates the Worker record from their paperwork"}. The worker gets their assignment notice with login details.</p>
                </>
              )}
              <button onClick={createAssignment} disabled={busy} data-testid="packet-create-assignment-btn"
                className={`mt-3 h-11 px-4 text-white font-bold rounded-md text-sm flex items-center gap-2 disabled:opacity-60 ${p.auto_assign_status === "held" ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
                <ArrowRight size={16} weight="bold" /> {busy ? "Creating…" : (p.auto_assign_status === "held" ? "Try again — create assignment" : "Create assignment")}
              </button>
            </div>
          )}
        </>
      ) : null}

      {isAdmin && (
        <div className="mt-6 pt-4 border-t border-slate-200 flex justify-end">
          <button onClick={del} data-testid="delete-packet-btn"
            className="h-10 px-3 text-rose-600 hover:text-rose-800 font-semibold text-sm flex items-center gap-1">
            <Trash size={14} weight="bold" /> Delete packet
          </button>
        </div>
      )}
    </div>
  );
}
